// Libraries
import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  Input,
  OnDestroy,
  signal,
  Signal
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormBuilder, FormControl } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import {
  FloatLabelType,
  MatFormFieldModule,
} from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router, RouterLink } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { first, forkJoin, Subject, Subscription } from 'rxjs';
import { map, switchMap, takeUntil } from 'rxjs/operators';

// Services
import { ProjectService } from 'src/app/services/api/projects/project.service';
import { UserService } from 'src/app/services/api/users/user.service';
import { AuthService } from 'src/app/services/core/auth.service';
import { ThemeService } from 'src/app/services/core/theme/theme.service';
import { CompanyStore } from 'src/app/services/stores/company.store';

// Interfaces
import { CompanyLight } from 'src/app/interfaces/api/company';
import { Project } from 'src/app/interfaces/api/project';
import { User } from 'src/app/interfaces/api/user';

// Classes
import { Pop } from 'src/app/classes/shared/pop';
import { SidenavStore } from 'src/app/services/stores/sidenav.store';
import { MainMenu } from './MainMenu';

// UI
import { UserAvatarComponent } from 'projects/ui-library/src/lib/components/user-avatar/user-avatar.component';
import { UiTool } from 'src/app/classes/shared/ui.tools';
import { ButtonComponent, DropdownMenuComponent, SpinnerComponent, TreeNode } from 'ui-library';

// Components
import { NotifyComponent } from 'src/app/components/common/notify/notify.component';
import { CompanyLogoComponent } from 'src/app/components/shared/company-logo/company-logo.component';
import { Terms } from 'src/app/components/shared/dialogs/terms/terms.component';
import { WelcomeComponent } from 'src/app/components/shared/dialogs/welcome/welcome.component';
import { NavigationService } from 'src/app/services/core/navigation/navigation.service';

@Component({
  selector: 'app-header',
  imports: [
    NotifyComponent,
    ButtonComponent,
    MatToolbarModule,
    MatButtonModule,
    MatIconModule,
    MatMenuModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    CommonModule,
    TranslateModule,
    DropdownMenuComponent,
    RouterLink,
    UserAvatarComponent,
    SpinnerComponent,
    CompanyLogoComponent,
    MatTooltipModule,
  ],
  templateUrl: './header.component.html',
  standalone: true,
  styleUrl: './header.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})

export class HeaderComponent implements OnDestroy, AfterViewInit {
  // Services
  private readonly authService: AuthService = inject(AuthService);
  private readonly userService: UserService = inject(UserService);
  private readonly projectService: ProjectService = inject(ProjectService);
  private readonly translate: TranslateService = inject(TranslateService);
  private readonly router: Router = inject(Router);
  private readonly SidenavStore: SidenavStore = inject(SidenavStore)
  private readonly themeService: ThemeService = inject(ThemeService);
  private readonly dialog: MatDialog = inject(MatDialog);
  private readonly pop: Pop = inject(Pop);
  private readonly navigationService = inject(NavigationService);
  private readonly companyStore = inject(CompanyStore);
  public readonly uiTool: UiTool = inject(UiTool);

  // Component API
  @Input() isMainLayout: boolean = false;

  // Variables
  user!: User;
  project!: Project;
  menuNodes: TreeNode[] = MainMenu.map(menu => ({ ...menu }));
  projectMenu!: TreeNode;
  mobileSubscription: Subscription = new Subscription();
  destroy$ = new Subject<void>();
  aspect: string = "square";
  isLandscape = signal<boolean>(false);

  private readonly BACK_WHITELIST = [
    '/project/dashboard',
    '/project/viewer/forge',
    '/project/viewer/maps',
    '/project/report',
    '/project/plannings',
    '/project/planning',
    '/project/risks',
    '/project/actions',
    '/project/timelapse',
    '/project/stream',
    '/project/project-settings/informations',
    '/project/project-settings/resources',
    '/project/workflows',
    '/project/project-settings/models',
    '/project/project-settings/users',
    '/project/project-settings/security',
    '/project/project-settings/tags',
    '/project/project-settings/database',
    '/project/project-settings/kyd',
  ];

  // Signals
  readonly currentProject: Signal<Project> = this.projectService.currentProjectSignal;
  readonly projectsVisited: Signal<any[]> = this.authService.projectsVisited;
  readonly currentUser: Signal<User> = this.authService.currentUser;
  readonly isLoadingProject: Signal<boolean> = this.projectService.isLoadingProject;
  readonly isMobileSignal: Signal<boolean> = this.uiTool.isMobileSignal;
  readonly currentCompany = this.companyStore.current;
  readonly menuCompanies = this.companyStore.menuCompanies;
  readonly isPrivileged = this.authService.isPrivileged;

  //Computed
  readonly lastProjects = computed(() =>
    [...this.projectsVisited()].slice(-10).reverse()
  );

  readonly favouriteProjects = computed(() =>
    this.authService.currentUser().favouriteProjects ?? []
  );

  private orientationHandler = () => this.handleOrientationChange();

  constructor() {
    // Effect: Update user changes
    effect(() => {
      const user = this.currentUser();
      this.user = user;

      if (user && user.id) {
        this.listProjects();
        this.checkTermApproved();
        this.themeService.initTheme(user.theme);

        if (user.language)
          this.translate.use(user.language);

        const userRole = user?.role?.name;

        this.menuNodes = MainMenu.map(menu => ({
          ...menu,
          children: menu.children
            ? menu.children.filter(child => !child.role || child.role.includes(userRole))
            : undefined
        })).filter(menu => !menu.role || menu.role.includes(userRole));

        this.projectMenu = this.menuNodes.find(menu => menu.translate == 'projects.title') as TreeNode;
      }
    });

    // Effect: Handle mobile sidenav state
    effect(() => {
      const isMobile = this.isMobileSignal();

      if (isMobile) {
        this.SidenavStore.closeSidenav();
      } else {
        this.SidenavStore.openSidenav();
      }
    });
  }

  ngAfterViewInit(): void {
    this.listenToOrientationChanges();
    this.handleOrientationChange();
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
    if (screen.orientation)
      screen.orientation.removeEventListener('change', this.orientationHandler);
    else
      window.removeEventListener("resize", this.orientationHandler);
  }

  private listenToOrientationChanges() {
    if (screen.orientation) {
      screen.orientation.addEventListener('change', this.orientationHandler);
    } else {
      window.addEventListener("resize", this.orientationHandler)
    }
  }

  private handleOrientationChange() {
    const width = window.innerWidth;
    const height = window.innerHeight;

    if (Math.abs(width - height) < 50 || width > 768) {
      this.aspect = "square";
      this.isLandscape.set(false);
    } else if (!screen.orientation) {
      this.isLandscape.set(width > height);
      if (this.isLandscape()) {
        this.aspect = "landscape";
      } else
        this.aspect = "portrait";
    } else {
      const type = screen.orientation.type;
      if (type.includes('landscape')) {
        this.aspect = "landscape";
        this.isLandscape.set(true);
      } else if (type.includes('portrait')) {
        this.aspect = 'portrait';
        this.isLandscape.set(false);
      }
    }

    window.dispatchEvent(new CustomEvent("aspect-change", {
      detail: {
        aspect: this.aspect,
        isLandscape: this.isLandscape()
      }
    }));
  }

  async buildMenu() {
  }

  selectCurrentProject(projectId: string) {
    const currentProjectId = this.projectService.currentProjectValue?.id;

    if (currentProjectId === projectId) {
      this.router.navigate(['/project/dashboard']);
      return;
    }

    // Start loading
    this.projectService.startLoading();

    // Load project details and status/activity in parallel
    this.projectService.getProjectById(projectId)
      .pipe(
        takeUntil(this.destroy$),
        switchMap((project: any) => {
          this.project = project;

          return forkJoin({
            status: this.projectService.getStatus(project.database),
            activity: this.projectService.getActivity({ databases: project.database })
          }).pipe(
            map(results => {
              project.status = results.status[0];
              project.activity = results.activity;
              return project;
            })
          );
        })
      )
      .subscribe({
        next: (updatedProject) => {
          // Set current project in service
          this.projectService.setCurrentProject(updatedProject);

          // Update visited projects and user data
          this.userService.setCurrentProject(projectId).pipe(first()).subscribe({
            next: (res) => {
              if (res) {
                let visited = [...this.projectsVisited()];
                const index = visited.indexOf(projectId);
                if (index > -1) visited.splice(index, 1);
                visited.push(projectId);
                this.userService.addVisitedProjects(visited);

                const redirectPaths = [
                  '/', '/projects', '/project-detail', '/projects-status',
                  '/project-dashboard', '/devices', '/users/accesses',
                  '/users/registry', '/users/edit-user', '/help',
                  '/profile', '/contact-us', '/chat', '/workflows-master'
                ];

                const currentUrl = (this.router.url?.split('?')[0]) || '';
                if (redirectPaths.includes(currentUrl)) {
                  this.router.navigate(['/project/dashboard']);
                }
              }

              this.projectService.stopLoading();
            },
            error: () => {
              this.projectService.stopLoading();
            }
          });
        },
        error: (err) => {
          console.error('❌ Error loading project:', err);
          this.projectService.stopLoading();
        }
      });
  }

  setCurrentProject(projectId: string) {
    this.selectCurrentProject(projectId);
  }

  onToggleSidenav() {
    this.SidenavStore.toggleSidenav();
  }

  readonly floatLabelControl = new FormControl('auto' as FloatLabelType);
  readonly options = inject(FormBuilder).group({
    floatLabel: this.floatLabelControl,
  });

  readonly floatLabel = toSignal(
    this.floatLabelControl.valueChanges.pipe(map((v) => v || 'auto')),
    { initialValue: 'auto' }
  );

  logout() {
    this.authService.logout();
    this.pop.toast(this.translate.instant('menu.user.logout_success'), 'success', 40000, 'logout');
  }

  selectCompany(company: CompanyLight) {
    this.companyStore.select(company);
  }

  onChatOpen() {
    this.router.navigate(['/chat'])
  }

  listProjects() {
    if (!this.user || !this.user.id) {
      return;
    }

    let query = {
      owner: this.user.id,
      author: this.user.id,
      users: this.user.id,
      limit: 500,
      archived: true,
    }

    if (!this.menuNodes.length)
      this.menuNodes = MainMenu.map(menu => ({ ...menu }));

    this.projectService.getAllProjectByUser(query).pipe(first()).subscribe((projects: Project[]) => {
      this.menuNodes.forEach(menu => {
        if (menu.translate == 'projects.title') {
          let children: TreeNode[] = [];
          menu.children = menu.children?.filter(subMenu => subMenu.name.includes("projects.")) as TreeNode[];

          for (let i = 0; i < this.favouriteProjects().length; i++) {
            let proj = projects.find(t => t.id == this.favouriteProjects()[i]);

            if (proj) {
              children.push({
                name: proj.name.length <= 45 ? proj.name : proj.name.slice(0, 45).trim() + "...",
                icon: 'bookmark',
                divide: i == this.favouriteProjects.length - 1 ? true : false,
                onClick: () => this.selectCurrentProject(proj.id),
                onIconClick: () => this.deleteProjectToFavorites(proj.id),
                expanded: false,
                role: ["admin", "dev", "guest", "user"]
              })
            }
          }

          menu.children = children.concat([...menu.children as TreeNode[]])

          if (menu.children)
            menu.children.forEach((child) => {
              if (child.translate == "projects.recents") {
                child.children = [];

                for (let i = 0; i < this.lastProjects().length; i++) {
                  let proj = projects.find(t => t.id == this.lastProjects()[i]);

                  if (proj) {
                    child.children.push({
                      name: proj.name,
                      onClick: () => this.selectCurrentProject(proj.id),
                      expanded: false,
                      role: ["admin", "dev", "guest", "user"]
                    })
                  }
                }
              }
            })
        }
      })
    })
  }

  deleteProjectToFavorites(project: any) {
    this.pop.confirm(this.translate.instant('projects.remove_favorite'), (confirm: any) => {
      if (confirm.ok) {
        const projectId = project.url ? project.url.split("=")[1] : project;
        const searchProject = this.favouriteProjects().includes(projectId);

        if (searchProject) {
          const index = this.favouriteProjects().indexOf(projectId);

          if (index > -1) {
            const updatedFavs = [...this.favouriteProjects()];
            updatedFavs.splice(index, 1);
            this.userService.updateFavouriteProjects(updatedFavs);
          }
        }
      }
    });
  }

  checkWelcome() {
    if (this.user && (this.user.welcomeViewed === false || this.user.welcomeViewed === undefined)) {
      const dialogRef = this.dialog.open(WelcomeComponent, {
        width: '1000px',
        disableClose: true,
        data: { language: this.user.language }
      })

      dialogRef.afterClosed().subscribe((result) => {
        if (result.action == 'confirm') {
          const user = {
            id: this.user.id,
            welcomeViewed: true
          }

          this.userService.setUser(user).pipe(takeUntil(this.destroy$)).subscribe(res => {
            this.user.welcomeViewed = true;
            this.authService.setUser(this.user)
          })
        } else if (result.action == 'dismissed') {
          this.logout();
        }
      });
    }
  }

  checkTermApproved() {
    if (this.user && (this.user.termsApproved === false || this.user.termsApproved === undefined)) {
      const dialogRef = this.dialog.open(Terms, {
        width: '1000px',
        maxHeight: '80vh',
        disableClose: true,
        data: { language: this.user.language }
      })

      dialogRef.afterClosed().subscribe(result => {
        if (result.action == 'confirm') {
          const user = {
            id: this.user.id,
            termsApproved: true,
            welcomeViewed: true
          }

          this.userService.setUser(user).pipe(takeUntil(this.destroy$)).subscribe(res => {
            this.user.termsApproved = true;
            this.user.welcomeViewed = true;
            this.authService.setUser(this.user)
          })
        } else if (result.action == 'dismissed') {
          this.logout();
        }
      });
    } else {
      this.checkWelcome();
    }
  }

  goHome() {
    this.router.navigate([this.currentProject()?.id ? '/project/dashboard' : '/projects']);
  }

  goBack() {
    const prev = this.navigationService.navigateBack();
    const isValid = prev && this.BACK_WHITELIST.some(p => prev.startsWith(p));
    this.router.navigateByUrl(isValid ? prev! : '/project/dashboard');
  }

}